/**
 * @handles: the unique name friends type to find someone.
 *
 * Mirrors `sshare_proto::social::handle_problem`, so a typo is caught while
 * typing instead of after a round trip. The server checks again; it is the
 * authority, because it alone knows who already has a handle.
 */

export const HANDLE_MIN = 3
export const HANDLE_MAX = 20

const RESERVED = [
  'admin',
  'administrator',
  'support',
  'help',
  'sofa',
  'sshare',
  'staff',
  'system',
  'root',
  'moderator',
  'official',
  'security',
  'everyone',
  'here',
]

/** What a person typed, as the handle it names: no `@`, lowercase. */
export function normalizeHandle(input: string): string {
  return input.trim().replace(/^@+/, '').toLowerCase()
}

/** Why `handle` (already normalised) cannot be used, or null when it is fine. */
export function handleProblem(handle: string): string | null {
  if (handle.length < HANDLE_MIN) return 'Use at least 3 characters'
  if (handle.length > HANDLE_MAX) return 'Use at most 20 characters'
  if (!/^[a-z0-9_.]+$/.test(handle)) return 'Use only letters, numbers, _ and .'
  if (!/^[a-z0-9]/.test(handle) || !/[a-z0-9]$/.test(handle))
    return 'Start and end with a letter or number'
  if (handle.includes('..')) return 'No two dots in a row'
  if (RESERVED.includes(handle)) return 'That name is reserved'
  return null
}

/** A first guess at a handle from a display name: "João Silva" becomes "joao.silva". */
export function suggestHandle(name: string): string {
  const base = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, HANDLE_MAX)
    .replace(/\.+$/, '')
  const padded = base.length >= HANDLE_MIN ? base : `${base}user`.slice(0, HANDLE_MAX)
  return handleProblem(padded) ? 'user' + Math.floor(100 + Math.random() * 900) : padded
}
